import { useShow } from '@refinedev/core';
import { Show } from '@refinedev/antd';
import { theme } from 'antd';
import type { PersonDetailOut } from '@/api/generated/schemas';
import { bareShowContentProps } from '@/components/common/show/shared';
import { useShowTab } from '@/components/common/show/useShowTab';
import { BulkModStatusControl } from '@/components/user_catalog/BulkModStatusControl';
import { useRecordDocumentTitle } from '@/hooks/useRecordDocumentTitle';
import { PERSON_DEFAULT_TAB, PERSON_SHOW_TABS } from '../constants';
import { PersonShowHeader } from './PersonShowHeader';
import { PersonGeneralTab } from './PersonGeneralTab';
import { PersonCollectionTab } from './PersonCollectionTab';
import { SearchPopularityCard } from '@/components/common/show/SearchPopularityCard';
import { CollectionPublicationsTable } from '@/components/user_catalog/collections/CollectionPublicationsTable';

type PersonShowProps = {
  resource: string;
  /** Kept separate from `resource` so label lookups stay greppable. */
  ns: string;
};

export const PersonShow = ({ resource, ns }: PersonShowProps) => {
  const { token } = theme.useToken();
  const { query } = useShow<PersonDetailOut>({ resource });
  const { data, isLoading } = query;
  const record = data?.data;

  useRecordDocumentTitle(resource, record?.name);

  const [activeTab, onTabChange] = useShowTab(
    PERSON_SHOW_TABS,
    PERSON_DEFAULT_TAB
  );

  return (
    <Show
      isLoading={isLoading}
      contentProps={bareShowContentProps}
      headerButtons={({ defaultButtons }) => (
        <>
          {record?.id != null && (
            <BulkModStatusControl
              resource={resource}
              ids={[record.id]}
              labelOf={() => record.name}
              onSuccess={() => query.refetch()}
            />
          )}
          {defaultButtons}
        </>
      )}
    >
      {record && (
        <>
          <PersonShowHeader
            record={record}
            ns={ns}
            activeTab={activeTab}
            onTabChange={onTabChange}
          />
          <div style={{ marginTop: token.margin }}>
            {activeTab === 'general' && (
              <PersonGeneralTab record={record} ns={ns} />
            )}
            {activeTab === 'publications' && (
              <CollectionPublicationsTable
                collectionId={record.id}
                collectionKind={record.collection_kind}
              />
            )}
            {activeTab === 'collection' && (
              <PersonCollectionTab record={record} ns={ns} />
            )}
            {activeTab === 'search' && (
              <SearchPopularityCard value={record.search_popularity} />
            )}
          </div>
        </>
      )}
    </Show>
  );
};
